import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Card from '../../components/common/Card';
import { getAllBadges, getMyGamification } from '../../services/gamificationService';
import { useCalendar } from '../../context/CalendarContext';
import { MdLock, MdCheckCircle, MdEmojiEvents } from 'react-icons/md';

// ── Rarity config ─────────────────────────────────────────────────────────────
const RARITY = {
  common:    { border: 'border-gray-200',   pill: 'bg-gray-100 text-gray-600',      glow: '',                         label: 'Common'    },
  rare:      { border: 'border-blue-300',   pill: 'bg-blue-100 text-blue-700',      glow: 'shadow-sm shadow-blue-100', label: 'Rare'      },
  epic:      { border: 'border-purple-300', pill: 'bg-purple-100 text-purple-700',  glow: 'shadow-sm shadow-purple-100',label: 'Epic'     },
  legendary: { border: 'border-yellow-400', pill: 'bg-yellow-100 text-yellow-700',  glow: 'shadow-md shadow-yellow-200',label: 'Legendary'},
};

// ── Categories ────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: 'all',        label: 'All'        },
  { id: 'attendance', label: 'Attendance' },
  { id: 'streak',     label: 'Streak'     },
  { id: 'belt',       label: 'Belt'       },
  { id: 'level',      label: 'Level'      },
  { id: 'payment',    label: 'Payment'    },
  { id: 'special',    label: 'Special'    },
  { id: 'trainer',    label: 'Trainer'    },
];

const RARITIES = ['all', 'common', 'rare', 'epic', 'legendary'];

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null;

// ── Badge progress for locked badges ─────────────────────────────────────────
function getBadgeProgress(badge, stats) {
  if (!badge.progressType || !stats) return null;
  const map = {
    attendance_count:  stats.totalAttendances,
    streak:            stats.streak,
    level:             stats.level,
    grading_passed:    stats.totalPassed,
    grading_count:     stats.totalGradings,
    payment_count:     stats.totalPayments,
    membership_months: stats.membershipMonths,
  };
  const current = map[badge.progressType] ?? 0;
  const target  = badge.progressTarget ?? 0;
  const pct     = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;
  return { current, target, pct };
}

// ── Level XP bar ──────────────────────────────────────────────────────────────
function LevelBar({ g }) {
  if (!g) return null;
  const pct = g.xpForLevel > 0 ? Math.min(100, Math.round((g.xpInLevel / g.xpForLevel) * 100)) : 100;
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-gray-500">
          Level {g.level} <span className="font-semibold text-secondary">{g.levelName}</span>
        </span>
        <span className="text-xs font-semibold text-primary">
          {g.xpPoints?.toLocaleString()} XP
        </span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
        <div
          className="h-2 rounded-full bg-gradient-to-r from-primary to-orange-400 transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-[11px] text-gray-400 mt-0.5">
        {g.isMaxLevel
          ? 'Maximum level reached!'
          : `${g.xpToNextLevel?.toLocaleString()} XP to Level ${g.level + 1} · ${g.nextLevelName}`}
      </p>
    </div>
  );
}

// ── Individual badge card ─────────────────────────────────────────────────────
function BadgeCard({ badge, stats }) {
  const { formatDate } = useCalendar();
  const r        = RARITY[badge.rarity] || RARITY.common;
  const progress = badge.earned ? null : getBadgeProgress(badge, stats);
  const isTrainerOnly = badge.requiresManualAward;

  return (
    <div
      className={`rounded-2xl border p-4 flex flex-col transition-all ${
        badge.earned
          ? `${r.border} bg-white ${r.glow}`
          : isTrainerOnly
            ? 'border-gray-200 bg-gray-50'
            : 'border-gray-200 bg-gray-50'
      }`}
    >
      {/* Icon */}
      <div className="flex items-start justify-between mb-2">
        <span className={`text-4xl leading-none ${badge.earned ? '' : 'opacity-50'}`}>
          {badge.icon}
        </span>
        {badge.earned
          ? <MdCheckCircle className="text-green-500 text-lg flex-shrink-0" />
          : <MdLock className={`text-base flex-shrink-0 ${isTrainerOnly ? 'text-purple-300' : 'text-gray-300'}`} />
        }
      </div>

      {/* Name + description */}
      <p className={`font-semibold text-sm leading-tight mb-1 ${badge.earned ? 'text-secondary' : 'text-gray-600'}`}>
        {badge.name}
      </p>
      <p className={`text-xs leading-snug mb-2 flex-1 ${badge.earned ? 'text-gray-500' : 'text-gray-400'}`}>
        {badge.description}
      </p>

      {/* Rarity + XP */}
      <div className="flex items-center gap-1.5 flex-wrap mb-2">
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full capitalize ${r.pill}`}>
          {r.label}
        </span>
        {badge.xpReward > 0 && (
          <span className={`text-[10px] font-semibold ${badge.earned ? 'text-yellow-600' : 'text-gray-400'}`}>
            +{badge.xpReward} XP
          </span>
        )}
      </div>

      {/* Status / progress */}
      {badge.earned ? (
        <p className="text-[10px] text-green-600 font-semibold">
          ✓ Earned {fmtDate(badge.earnedAt) || formatDate(badge.earnedAt)}
        </p>
      ) : isTrainerOnly ? (
        <p className="text-[10px] text-purple-500 font-medium leading-snug">
          Show discipline, respect &amp; effort in class — your trainer will recognize you.
        </p>
      ) : progress ? (
        <div>
          <div className="w-full bg-gray-200 rounded-full h-1.5 mb-1 overflow-hidden">
            <div
              className="h-1.5 rounded-full bg-red-500/60 transition-all"
              style={{ width: `${progress.pct}%` }}
            />
          </div>
          <p className="text-[10px] text-gray-400">
            {progress.current} / {progress.target}
          </p>
        </div>
      ) : (
        <p className="text-[10px] text-gray-400">Not yet earned</p>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
function BadgeGallery() {
  document.title = 'My Badges — TrainAxis';
  const [category, setCategory] = useState('all');
  const [rarity,   setRarity]   = useState('all');

  const { data: badgesData, isPending } = useQuery({
    queryKey: ['all-badges'],
    queryFn:  () => getAllBadges().then(r => r.data),
  });

  const { data: statsData } = useQuery({
    queryKey: ['my-gamification'],
    queryFn:  () => getMyGamification().then(r => r.data),
    staleTime: 60000,
  });

  const g      = statsData?.gamification;
  const stats  = g ? { ...g.stats, streak: g.streak, level: g.level } : null;
  const allBadges = badgesData?.badges || [];

  const filtered = allBadges.filter(b => {
    const catOk = category === 'all' || b.category === category;
    const rarOk = rarity   === 'all' || b.rarity   === rarity;
    return catOk && rarOk;
  });

  const earnedCount = allBadges.filter(b => b.earned).length;
  const totalCount  = allBadges.length;
  const earnedPct   = totalCount > 0 ? Math.round((earnedCount / totalCount) * 100) : 0;

  return (
    <div className="space-y-5 pb-10">
      {/* Level + XP banner */}
      {g && (
        <Card>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="text-center flex-shrink-0">
              <p className="text-2xl font-bold text-secondary leading-none">{g.level}</p>
              <p className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5">Level</p>
            </div>
            <div className="w-px h-10 bg-gray-100 flex-shrink-0" />
            <div className="text-center flex-shrink-0">
              <p className="text-2xl font-bold text-orange-500 leading-none">🔥 {g.streak}</p>
              <p className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5">Streak</p>
            </div>
            <div className="w-px h-10 bg-gray-100 flex-shrink-0 hidden sm:block" />
            <LevelBar g={g} />
          </div>
        </Card>
      )}

      {/* Progress summary */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-bold text-secondary">
            {earnedCount} <span className="font-normal text-gray-400">/ {totalCount} badges earned</span>
          </p>
          <div className="w-48 bg-gray-100 rounded-full h-1.5 mt-1 overflow-hidden">
            <div
              className="h-1.5 rounded-full bg-gradient-to-r from-primary to-orange-400"
              style={{ width: `${earnedPct}%` }}
            />
          </div>
        </div>
        {/* Rarity filter */}
        <div className="flex gap-1 flex-wrap">
          {RARITIES.map(r => (
            <button
              key={r}
              onClick={() => setRarity(r)}
              className={`px-2.5 py-1 rounded-xl text-[11px] font-semibold capitalize transition-colors ${
                rarity === r ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {r === 'all' ? 'All Rarities' : r}
            </button>
          ))}
        </div>
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {CATEGORIES.map(c => (
          <button
            key={c.id}
            onClick={() => setCategory(c.id)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors border ${
              category === c.id
                ? 'bg-primary text-white border-primary'
                : 'border-gray-200 text-gray-500 hover:border-primary hover:text-primary bg-white'
            }`}
          >
            {c.label}
            {c.id !== 'all' && (
              <span className="ml-1.5 text-[10px] opacity-70">
                {allBadges.filter(b => b.category === c.id && b.earned).length}/
                {allBadges.filter(b => b.category === c.id).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Badge grid */}
      {isPending ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-44 bg-gray-100 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <div className="py-10 text-center">
            <MdEmojiEvents className="text-4xl text-gray-200 mx-auto mb-3" />
            <p className="text-sm font-semibold text-gray-500">No badges match this filter</p>
            <button
              onClick={() => { setCategory('all'); setRarity('all'); }}
              className="text-xs text-primary hover:underline mt-2 inline-block"
            >
              Clear filters
            </button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {/* Earned first, then locked */}
          {[...filtered.filter(b => b.earned), ...filtered.filter(b => !b.earned)]
            .map(badge => (
              <BadgeCard key={badge.id} badge={badge} stats={stats} />
            ))
          }
        </div>
      )}

      {/* Empty state when no badges earned yet */}
      {!isPending && earnedCount === 0 && category === 'all' && (
        <Card>
          <div className="py-10 text-center">
            <span className="text-5xl">🥋</span>
            <p className="text-sm font-semibold text-secondary mt-3">No badges earned yet</p>
            <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto">
              Attend your first class to unlock your first badge. Every legend starts somewhere.
            </p>
          </div>
        </Card>
      )}

      {/* Trainer recognition note */}
      {(category === 'all' || category === 'trainer') && (
        <Card className="border-purple-100 bg-purple-50/40">
          <p className="text-xs text-purple-700 leading-relaxed">
            <span className="font-semibold">🎖️ Trainer Recognition badges</span> are awarded by your trainer or admin for
            outstanding attitude, leadership, and respect — qualities that cannot be measured by attendance alone.
            Focus on doing your best every class and these will come naturally.
          </p>
        </Card>
      )}
    </div>
  );
}

export default BadgeGallery;
